import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ActionWithStatus, ActionInput, BatchExecutionRequestInput } from '@databro/shared';
import {
  Button,
  Input,
  Label,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ScrollArea,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@databro/ui';

type RunMode = 'single' | 'batch';

interface RunActionModalProps {
  action: ActionWithStatus;
  onClose: () => void;
  onSuccess: (executionId: string) => void;
}

export default function RunActionModal({ action, onClose, onSuccess }: RunActionModalProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<RunMode>('single');
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [error, setError] = useState('');

  // Batch mode state
  const [batchStartDate, setBatchStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [batchEndDate, setBatchEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  });
  const [batchTimezone, setBatchTimezone] = useState('Asia/Jakarta');

  const { data: inputsData, isLoading } = useQuery({
    queryKey: ['action-inputs', action.id],
    queryFn: () => api.getActionInputs(action.id),
  });

  // Calculate batch days count
  const batchDaysCount = useMemo(() => {
    try {
      const start = new Date(batchStartDate);
      const end = new Date(batchEndDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
      const diffTime = end.getTime() - start.getTime();
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } catch {
      return 0;
    }
  }, [batchStartDate, batchEndDate]);

  useEffect(() => {
    if (inputsData?.inputs) {
      const defaults: Record<string, unknown> = {};
      for (const input of inputsData.inputs) {
        if (input.default !== undefined) {
          defaults[input.name] = input.default;
        } else {
          defaults[input.name] = getDefaultValue(input);
        }
      }
      setFormValues(defaults);
    }
  }, [inputsData]);

  const runMutation = useMutation({
    mutationFn: () => api.runAction(action.id, formValues),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      queryClient.invalidateQueries({ queryKey: ['executions'] });
      onSuccess(data.executionId);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to run action');
    },
  });

  const runBatchMutation = useMutation({
    mutationFn: (params: BatchExecutionRequestInput) => api.runActionBatch(action.id, params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      queryClient.invalidateQueries({ queryKey: ['executions'] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      // Navigate to first execution or batch view
      if (data.executions.length > 0) {
        onSuccess(data.executions[0].id);
      } else {
        onClose();
      }
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to run batch');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'single') {
      runMutation.mutate();
    } else {
      if (batchDaysCount === 0) {
        setError('Invalid date range');
        return;
      }
      runBatchMutation.mutate({
        type: 'date_range',
        startDate: batchStartDate,
        endDate: batchEndDate,
        timezone: batchTimezone,
      });
    }
  };

  const updateValue = (name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const isPending = runMutation.isPending || runBatchMutation.isPending;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Run Action</DialogTitle>
          <DialogDescription>{action.name}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <Tabs value={mode} onValueChange={(v) => setMode(v as RunMode)} className="flex flex-col flex-1 overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="single">Single Run</TabsTrigger>
              <TabsTrigger value="batch">Batch</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full pr-4">
                <div className="space-y-4 py-2">
                  {isLoading ? (
                    <div className="animate-pulse text-muted-foreground">Loading inputs...</div>
                  ) : inputsData?.inputs.length === 0 ? (
                    <p className="text-muted-foreground">No configurable inputs for this action.</p>
                  ) : (
                    inputsData?.inputs.map((input) => (
                      <InputField
                        key={input.name}
                        input={input}
                        value={formValues[input.name]}
                        onChange={(value) => updateValue(input.name, value)}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="batch" className="flex-1 overflow-hidden mt-0">
              <ScrollArea className="h-full pr-4">
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Batch Type</Label>
                    <Select value="date_range" disabled>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="date_range">Date Range</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Run the action once for each day in the date range.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={batchStartDate}
                      onChange={(e) => setBatchStartDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={batchEndDate}
                      onChange={(e) => setBatchEndDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select value={batchTimezone} onValueChange={setBatchTimezone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Asia/Jakarta">Asia/Jakarta (UTC+7)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                        <SelectItem value="Asia/Singapore">Asia/Singapore (UTC+8)</SelectItem>
                        <SelectItem value="Asia/Bangkok">Asia/Bangkok (UTC+7)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Timezone for calculating day_start and day_end timestamps.
                    </p>
                  </div>

                  {batchDaysCount > 0 && (
                    <div className="p-3 bg-muted rounded-md">
                      <p className="text-sm">
                        Will create <span className="font-semibold">{batchDaysCount}</span> execution{batchDaysCount !== 1 ? 's' : ''}.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Each execution will run with a 3-8 second delay between them.
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {error && (
            <div className="text-destructive text-sm mt-2">{error}</div>
          )}

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || (mode === 'batch' && batchDaysCount === 0)}>
              {isPending
                ? 'Running...'
                : mode === 'single'
                  ? 'Run'
                  : `Run Batch (${batchDaysCount})`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InputField({
  input,
  value,
  onChange,
}: {
  input: ActionInput;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const isEditable = input.editable !== false;
  const label = input.label || formatLabel(input.name);

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {input.required && <span className="text-destructive ml-1">*</span>}
      </Label>

      {input.description && (
        <p className="text-xs text-muted-foreground">{input.description}</p>
      )}

      {renderInput(input, value, onChange, isEditable)}
    </div>
  );
}

function renderInput(
  input: ActionInput,
  value: unknown,
  onChange: (value: unknown) => void,
  isEditable: boolean
) {
  switch (input.type) {
    case 'timestamp':
    case 'timestamp_ms':
    case 'datetime':
      return (
        <Input
          type="datetime-local"
          value={timestampToDatetimeLocal(value, input.type)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(datetimeLocalToValue(e.target.value, input.type))}
          disabled={!isEditable}
          required={input.required}
        />
      );

    case 'date':
      return (
        <Input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={!isEditable}
          required={input.required}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value ? Number(e.target.value) : '')}
          disabled={!isEditable}
          required={input.required}
        />
      );

    case 'boolean':
      return (
        <div className="flex items-center gap-2">
          <Checkbox
            checked={Boolean(value)}
            onCheckedChange={(checked: boolean | 'indeterminate') => onChange(checked === true)}
            disabled={!isEditable}
          />
          <span className="text-sm">Enabled</span>
        </div>
      );

    case 'select':
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          onValueChange={onChange}
          disabled={!isEditable}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {input.options?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    default:
      return (
        <Input
          value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={!isEditable}
          required={input.required}
        />
      );
  }
}

function getDefaultValue(input: ActionInput): unknown {
  const now = new Date();

  switch (input.type) {
    case 'timestamp':
      if (input.name === 'day_start') {
        const dayStart = new Date(now);
        dayStart.setHours(0, 0, 0, 0);
        return Math.floor(dayStart.getTime() / 1000);
      }
      if (input.name === 'day_end') {
        const dayEnd = new Date(now);
        dayEnd.setHours(23, 59, 59, 999);
        return Math.floor(dayEnd.getTime() / 1000);
      }
      return Math.floor(now.getTime() / 1000);

    case 'timestamp_ms':
      return now.getTime();

    case 'datetime':
      return now.toISOString();

    case 'date':
      return now.toISOString().split('T')[0];

    case 'number':
      if (input.name === 'year') return now.getFullYear();
      if (input.name === 'month') return now.getMonth() + 1;
      if (input.name === 'day') return now.getDate();
      return 0;

    case 'boolean':
      return false;

    default:
      return '';
  }
}

function timestampToDatetimeLocal(value: unknown, type: string): string {
  if (!value) return '';

  let date: Date;
  if (type === 'timestamp_ms') {
    date = new Date(Number(value));
  } else if (type === 'datetime' && typeof value === 'string') {
    date = new Date(value);
  } else {
    date = new Date(Number(value) * 1000);
  }

  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function datetimeLocalToValue(datetimeLocal: string, type: string): unknown {
  if (!datetimeLocal) return '';

  const date = new Date(datetimeLocal);
  if (isNaN(date.getTime())) return '';

  switch (type) {
    case 'timestamp_ms':
      return date.getTime();
    case 'datetime':
      return date.toISOString();
    default:
      return Math.floor(date.getTime() / 1000);
  }
}

function formatLabel(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
